import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  PlayCircle as PlayCircleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import '../../styles/StudentWall.css';
import StudentNavbar from '../../components/StudentNavbar';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../config.json';

const menuGroups = [
  {
    title: '我的學習',
    items: [
      { icon: InventoryIcon, text: '我的課程', active: true, onClick: () => window.location.href='/student/my-courses'},
      { icon: SchoolIcon, text: '我的社群', active: false, onClick: () => window.location.href='/student/my-classes'},
    ],
  },
];

export default function MyCourses() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('全部');
  const [subjectFilter, setSubjectFilter] = useState('全部');

  // 後端課程資料（任務/派發）
  const [tasks, setTasks] = useState([]);

  const safeBase64UrlDecode = (base64url) => {
    try {
      const base64 = (base64url || '').replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '==='.slice((base64.length + 3) % 4);
      return atob(padded);
    } catch (e) {
      console.warn('[MyCourses] safeBase64UrlDecode 失敗:', e?.message);
      return '';
    }
  };

  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const json = safeBase64UrlDecode(parts[1]);
      const payload = JSON.parse(json || '{}');
      const uid = (
        payload.id ||
        payload.userId ||
        payload.dataValues?.id ||
        payload._previousDataValues?.id ||
        payload._prevDataValues?.id ||
        null
      );
      console.log('[MyCourses] 解析使用者 ID:', uid, 'payload keys:', Object.keys(payload || {}));
      return uid;
    } catch (e) {
      console.warn('[MyCourses] 解析 JWT 失敗:', e?.message);
      return null;
    }
  };

  // 載入學生加入之社群 → 各社群的課程派發（使用後端真實資料，遵循 config.json）
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('[MyCourses] 取得用戶社群 API:', config.getUserClasses);
        const ucResp = await axiosInstance.get(config.getUserClasses);
        console.log('[MyCourses] 用戶社群原始回應:', ucResp.data);
        const userClasses = ucResp.data?.data || ucResp.data || [];
        const classIds = (Array.isArray(userClasses) ? userClasses : []).map(c => c.classId || c.class_id || c.Class?.id).filter(Boolean);
        console.log('[MyCourses] 社群數量:', classIds.length, classIds);
        // 從 userClasses 推導目前使用者 ID（後端已回傳 userId）
        const ucSelfIds = (Array.isArray(userClasses) ? userClasses : []).map(c => c.userId || c.UserId || c.user_id).filter(Boolean);
        const fallbackUid = ucSelfIds.length > 0 ? String(ucSelfIds[0]) : null;
        const meFromJwt = getCurrentUserId();
        const me = meFromJwt ? String(meFromJwt) : fallbackUid;
        console.log('[MyCourses] 當前使用者 ID 決定:', { meFromJwt, fallbackUid, final: me });

        // 依照每個社群取得課程派發
        const allDispatches = [];
        for (const classId of classIds) {
          const url = `${config.getClassCourseDispatches}${classId}`;
          console.log('[MyCourses] 取得社群課程派發 API:', url);
          try {
            const cdResp = await axiosInstance.get(url);
            console.log('[MyCourses] 社群課程派發回應:', cdResp.data);
            const cds = cdResp.data?.data || cdResp.data || [];
            for (const cd of cds) {
              // 嘗試取得「我的組別」
              let myGroupId = null;
              let myGroupName = null;
              try {
                if (me) {
                  const groupsUrl = `${config.getCourseDispatchGroups}${cd.id}/groups`;
                  console.log('[MyCourses] 請求課程分組 API:', groupsUrl);
                  const gResp = await axiosInstance.get(groupsUrl);
                  const groups = gResp.data?.data || [];
                  console.log('[MyCourses] 課程分組回應數量:', groups.length);
                  const mine = groups.find(g => Array.isArray(g.GroupMembers) && g.GroupMembers.some(m => String(m.user_id) === String(me) || String(m.User?.id || '') === String(me)));
                  if (mine) {
                    myGroupId = mine.id;
                    myGroupName = mine.groupName || mine.name || `第${mine.index || ''}組`;
                  } else {
                    console.log('[MyCourses] 未找到我的組別，me:', String(me), 'groups example:', groups[0]);
                  }
                }
              } catch (e) {
                console.warn('[MyCourses] 取得課程分組失敗:', e?.response?.status, e?.response?.data || e?.message);
              }

              allDispatches.push({
              id: cd.id,
              title: cd.title || cd.LessonPackage?.name || '未命名課程',
              description: cd.description || cd.LessonPackage?.description || '',
              subject: cd.LessonPackage?.subject || '-',
                year: cd.Class?.year || '-',
                semester: cd.Class?.semester || '-',
              status: cd.status || 'active',
                teacher: cd.teacher_name || '-',
              className: cd.Class?.name || '-',
              createdAt: cd.createdAt || cd.created_at,
                courseDispatchId: cd.id,
                myGroupId,
                myGroupName
              });
            }
          } catch (e) {
            console.log('[MyCourses] 載入課程派發失敗 (略過該社群):', classId, e?.response?.data);
          }
        }

        const assignedOnly = allDispatches.filter(d => {
          const keep = !!d.myGroupId;
          if (!keep) {
            console.log('[MyCourses] 過濾掉未分組課程:', {
              courseDispatchId: d.id,
              title: d.title,
              className: d.className,
              myGroupId: d.myGroupId,
              myGroupName: d.myGroupName
            });
          }
          return keep;
        });
        console.log('[MyCourses] 合併後課程派發數量(含已分組):', assignedOnly.length);
        setTasks(assignedOnly);
      } catch (err) {
        console.error('載入課程清單失敗:', err);
        console.log('[MyCourses] 錯誤回應內容:', err?.response?.data);
        setError(err?.response?.data?.message || '載入課程清單失敗');
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // 篩選課程資料
  const filteredTasks = tasks.filter(task => {
    const matchesSearch = !searchTerm || 
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.teacher.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.className.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '全部' || task.status === statusFilter;
    const matchesSubject = subjectFilter === '全部' || task.subject === subjectFilter;
    return matchesSearch && matchesStatus && matchesSubject;
  });

  const getSubjectOptions = () => {
    const subjects = [...new Set(tasks.map(task => task.subject))];
    return ['全部', ...subjects.sort()];
  };

  // 狀態顯示已移除
  const getStatusColor = () => 'default';
  const getStatusText = () => '';

  const handleTaskClick = (task) => {
    const studentGroupId = task.myGroupId || sessionStorage.getItem('groupId');
    if (studentGroupId) {
      navigate(`/student/idea-wall/${task.courseDispatchId}/${studentGroupId}`);
    }
  };

  const handleGroupChipClick = (task) => {
    if (task.myGroupId) {
      navigate(`/student/idea-wall/${task.courseDispatchId}/${task.myGroupId}`);
    }
  };

  return (
    <div className="teacher-layout my-tasks-container">
      <div className="main-content-v2">
        <StudentNavbar menuGroups={menuGroups} />
        <div className="class-header-row">
          <h1 className="main-title">我的課程</h1>
        </div>
        <div className="filter-container">
          <TextField
            placeholder="🔍 搜尋課程"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            className="filter-search-input"
          />
          <FormControl size="small" className="filter-select">
            <InputLabel>狀態</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="狀態"
            >
              <MenuItem value="全部">全部</MenuItem>
              <MenuItem value="pending">待開始</MenuItem>
              <MenuItem value="in_progress">進行中</MenuItem>
              <MenuItem value="completed">已完成</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" className="filter-select">
            <InputLabel>科目</InputLabel>
            <Select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              label="科目"
            >
              {getSubjectOptions().map(subject => (
                <MenuItem key={subject} value={subject}>{subject}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <button
            className="btn-secondary filter-clear-btn"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('全部');
              setSubjectFilter('全部');
            }}
          >
            清除篩選
          </button>
        </div>
        <div className="activity-card-list grid-responsive">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>載入中...</Typography>
            </div>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : filteredTasks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                目前沒有課程
              </Typography>
              <Typography variant="body2" color="text.secondary">
                請等待老師指派新的課程任務
              </Typography>
            </div>
          ) : (
            filteredTasks.map((task) => (
              <div
                className="activity-card"
                key={task.id}
                style={{ 
                  position: 'relative', 
                  background:'#fff', 
                  boxShadow:'0 2px 8px rgba(44,44,80,0.08)', 
                  borderRadius: '12px', 
                  border:'1.5px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  minHeight: '200px',
                  maxHeight: '200px',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(44,44,80,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(44,44,80,0.08)';
                }}
                onClick={() => handleTaskClick(task)}
              >
                <div className="activity-name" style={{ 
                  fontSize: '1.2rem', 
                  marginBottom: '8px',
                  padding: '0 8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  lineHeight: '1.4',
                  minHeight: '2.8rem'
                }}>
                  {task.title}
                </div>
                <div style={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  padding: '0 8px',
                  marginBottom: '8px'
                }}>
                  <Chip label={task.semester} size="small" color="info" variant="outlined"/>
                  <Chip label={task.year} size="small" color="primary" variant="outlined"/>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}>
                  <Chip
                    label={`我的組別：${task.myGroupName || '未分組'}`}
                    size="small"
                    color={task.myGroupId ? 'success' : 'default'}
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGroupChipClick(task);
                    }}
                  />
                </div>
                <div style={{
                  padding: '0 8px',
                  fontSize: '0.85rem',
                  color: '#666',
                  lineHeight: '1.3'
                }}>
                  <div style={{ marginBottom: '4px' }}>
                    老師：{task.teacher}
                  </div>
                  {/* 移除社群名稱顯示 */}
                </div>
                {/* 移除狀態角標 */}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
